import { css, cx } from '@linaria/atomic';
import { theme } from '@frontend/theme';
import { getFocusClassName } from '../button/styles/common.styles';
import { InlineLabelPlacement } from '../forms';
import { IconButtonSize } from './types';

const NORMAL = css`
  min-width: 40px;
  height: 40px;
  width: 40px;
  font-size: ${theme.fontSize(16)};
`;

const NORMAL_LABEL = css`
  min-width: 40px;
  height: 40px;
  width: auto;
  font-size: ${theme.fontSize(16)};
`;

const SMALL = css`
  min-width: 32px;
  height: 32px;
  width: 32px;
  font-size: ${theme.fontSize(14)};
`;

const SMALL_LABEL = css`
  min-width: 32px;
  height: 32px;
  width: auto;
  font-size: ${theme.fontSize(14)};
`;

const XSMALL = css`
  min-width: 24px;
  height: 24px;
  width: 24px;
  font-size: ${theme.fontSize(14)};
`;

const XSMALL_LABEL = css`
  min-width: 24px;
  height: 24px;
  width: auto;
  font-size: ${theme.fontSize(14)};
`;

const XXSMALL = css`
  min-width: 16px;
  height: 16px;
  width: 16px;
  font-size: ${theme.fontSize(14)};
`;

const XXSMALL_LABEL = css`
  min-width: 16px;
  height: 16px;
  width: auto;
  font-size: ${theme.fontSize(14)};
`;

const LABEL_RIGHT = css`
  flex-direction: row;
`;

const LABEL_LEFT = css`
  flex-direction: row-reverse;
`;

const DEFAULT = css`
  color: ${theme.colors.neutral80};
`;

const LIGHT = css`
  color: ${theme.colors.white};
`;

const DESTRUCTIVE = css`
  color: ${theme.colors.critical50};

  svg {
    fill: ${theme.colors.critical50};
  }
`;

const DISABLED = css`
  &[aria-disabled='true'] {
    cursor: default;
    color: ${theme.colors.neutral20};

    svg {
      fill: ${theme.colors.neutral20};
    }
  }
`;

const HOVER_DEFAULT = css`
  &:hover:not([aria-disabled='true']) {
    background: ${theme.colors.neutral5};
  }
`;

const HOVER_LIGHT = css`
  &:hover:not([aria-disabled='true']) {
    background: ${theme.colors.neutral5};
    color: ${theme.colors.neutral80};

    svg {
      fill: ${theme.colors.neutral80};
    }
  }
`;

const HOVER_DESTRUCTIVE = css`
  &:hover:not([aria-disabled='true']) {
    background: ${theme.colors.neutral5};
  }
`;

const LABEL_ALWAYS = css`
  border-radius: 4px;

  &::before {
    border-radius: 4px;
  }
`;

const SIZE_CLASS = {
  normal: {
    icon: NORMAL,
    label: NORMAL_LABEL,
  },
  small: {
    icon: SMALL,
    label: SMALL_LABEL,
  },
  xsmall: {
    icon: XSMALL,
    label: XSMALL_LABEL,
  },
  xxsmall: {
    icon: XXSMALL,
    label: XXSMALL_LABEL,
  },
} as const;

export const getIconButtonSizeClassName = (size: IconButtonSize, showLabelAlways?: boolean) =>
  SIZE_CLASS[size][showLabelAlways ? 'label' : 'icon'];

export const getLabelPlacementClassName = (labelPlacement?: InlineLabelPlacement) =>
  labelPlacement === 'left' ? LABEL_LEFT : LABEL_RIGHT;

export const getIconButtonDisabledClassName = () => DISABLED;

export const getIconButtonVariantClassName = ({
  color,
  destructive,
}: {
  color: 'default' | 'light';
  destructive?: boolean;
}) => {
  if (destructive) {
    return DESTRUCTIVE;
  }

  return color === 'light' ? LIGHT : DEFAULT;
};

export const getIconButtonHoverClassName = ({
  color,
  destructive,
}: {
  color: 'default' | 'light';
  destructive?: boolean;
}) => {
  if (destructive) {
    return HOVER_DESTRUCTIVE;
  }

  return color === 'light' ? HOVER_LIGHT : HOVER_DEFAULT;
};

export const iconButtonBase = css`
  padding: ${theme.spacing(1)};
  background-color: transparent;
  border: 1px solid transparent;
  border-radius: ${theme.borderRadius.small};
  display: flex;
  gap: ${theme.spacing(1)};
  align-items: center;
  justify-content: center;
  transition: background-color 250ms ease-in-out;
`;

export const iconButtonClassName = ({
  size,
  color,
  destructive,
  showLabelAlways,
  labelPlacement,
}: {
  size: IconButtonSize;
  color: 'default' | 'light';
  destructive?: boolean;
  showLabelAlways?: boolean;
  labelPlacement?: InlineLabelPlacement;
}) =>
  cx(
    iconButtonBase,
    getIconButtonSizeClassName(size, showLabelAlways),
    getLabelPlacementClassName(labelPlacement),
    getIconButtonVariantClassName({ color, destructive }),
    getIconButtonHoverClassName({ color, destructive }),
    DISABLED,
    getFocusClassName('primary'),
    showLabelAlways && LABEL_ALWAYS
  );
